/* eslint-disable max-len */
import { useField, useFormikContext } from 'formik';
import { find } from 'lodash';
import { useEffect, useState } from 'react';
import { fetchNonPublicStudentListItems } from 'src/redux/thunks/nonPublicStudentThunks';
import { abortPromiseOnUnmount } from 'src/services/base.service';
import { NonPublicStudentFormViewModel } from 'src/types/NonPublicStudentFormViewModel';
import { NonPublicStudentListItemModel } from 'src/types/NonPublicStudentListItemModel';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  selectNonPublicStudentDataEntryMode,
  selectNonPublicStudentFormViewModel,
  selectNonPublicStudentListItems,
  selectNonPublicStudentNonPublicStudent,
  selectSchoolYear,
  setHasSearchMatch,
  setHas200500SearchMatch,
  selectAllowEdit,
} from '../../../redux/slices/nonPublicStudentSlice';
import {
  getCurrentUserAccess,
  isValidCode,
} from '../../../services/base.service';
import { getStudentByMSISId } from '../../../services/nonPublicStudent.service';
import { ButtonSize } from '../../../types/ButtonSize';
import { DataEntryMode } from '../../../types/DataEntryMode';
import { NonPublicStudentEntryModel } from '../../../types/NonPublicStudentEntryModel';
import { NonPublicStudentSearchResultModel } from '../../../types/NonPublicStudentSearchResultModel';
import { PanelType } from '../../../types/PanelType';
import { NonPublicStudentEntryValidationSchema } from '../../../types/Validation/NonPublicStudentEntryValidationSchema';
import { isStateNonAdmin } from '../../../utilities/userUtilities';
import ActionButton from '../../ActionButton/ActionButton';
import FormDateSelector from '../../FormInputs/FormDateSelector/FormDateSelector';
import FormTextbox from '../../FormInputs/FormTextbox/FormTextbox';
import Panel from '../../Panel/Panel';
import './NPSE_StudentPanel.css';
import Modal  from '../../Modal/Modal';
import { DialogType } from '../../../types/DialogTypes';
import { MAX_DATE } from 'src/types/Constants';

const formatSSNForDisplay = (value: string, previousValue = ''): string => {
  if (!value) return '';
  const cleanValue = value.replace(/[^\d-]/g, '');
  const digitsOnly = cleanValue.replace(/\D/g, '');
  const truncatedDigits = digitsOnly.substring(0, 9);
  const userTypedDash = value.length > previousValue.length && value.endsWith('-');
  if (truncatedDigits.length === 0) return '';
  
  if (truncatedDigits.length <= 3) {
    return userTypedDash && truncatedDigits.length === 3 ? `${truncatedDigits}-` : truncatedDigits;
  } else if (truncatedDigits.length <= 5) {
    const part1 = truncatedDigits.substring(0, 3);
    const part2 = truncatedDigits.substring(3);
    const formatted = `${part1}-${part2}`;
    
    return userTypedDash && truncatedDigits.length === 5 ? `${formatted}-` : formatted;
  } else {
    const part1 = truncatedDigits.substring(0, 3);
    const part2 = truncatedDigits.substring(3, 5);
    const part3 = truncatedDigits.substring(5);
    return `${part1}-${part2}-${part3}`;
  }
};

const NPSE_StudentPanel : React.FC = () => {

  const { values, setFieldValue, setFieldTouched, dirty, validateField } =
    useFormikContext<NonPublicStudentEntryModel>();
  const dispatch = useAppDispatch();

  const [, msisIdMetaProps] = useField('studentIdentifier');
  const getIsMsisIDValid = (): boolean => !msisIdMetaProps?.error;

  const dataEntryMode: DataEntryMode = useAppSelector(
    selectNonPublicStudentDataEntryMode
  );

  const listItem: NonPublicStudentListItemModel[] = useAppSelector(
    selectNonPublicStudentListItems
  );

  const formView: NonPublicStudentFormViewModel = useAppSelector(
    selectNonPublicStudentFormViewModel
  );

  const npsInput: NonPublicStudentEntryModel = useAppSelector(
    selectNonPublicStudentNonPublicStudent
  );
  const newModeSchoolYear = useAppSelector(selectSchoolYear);

  const getDistrictId =
    getCurrentUserAccess()?.currentDistrict?.districtId || '';

  const hasMatch: boolean =
    dataEntryMode === DataEntryMode.EDIT;

  const allowEdit: boolean = useAppSelector(
    selectAllowEdit
  )

  const [searching, setSearching] = useState(false);
  const [searchMessage, setSearchMessage] = useState('');
  const [hasSearchMatch, setHasSearchMatchState] = useState(hasMatch);
  const [hasNon200500SearchMatch, setHasNon200500SearchMatchState] = useState(false);
  const [showStudentEnrolled, setShowStudentEnrolled] = useState(false);
  const [leaNameOfMsisFoundStudent, setLeaNameOfMsisFoundStudent] = useState('');

  const isMsisIdAndSchoolTypeNotEmpty: boolean =
    values.studentIdentifier !== '' && values.schoolType !== '';
  const areNonMsisFormComponentsDisabled: boolean =
    !hasSearchMatch || hasNon200500SearchMatch || !allowEdit || isStateNonAdmin();

  useEffect(() => {
    let promise: unknown;
    let schoolYear = '';

    if (DataEntryMode.NEW) {
      schoolYear = newModeSchoolYear || formView.defaultSchoolYear;
    } else {
      schoolYear = npsInput.schoolYear;
    }

    if (schoolYear) {
      promise = dispatch(fetchNonPublicStudentListItems(schoolYear));
    }
    return () => {
      abortPromiseOnUnmount(promise);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, newModeSchoolYear]);

  useEffect(() => {
    if (dataEntryMode === DataEntryMode.EDIT && values.studentIdentifier !== '') {
      dispatch(setHasSearchMatch(true));
    }
  }, [dataEntryMode, dispatch, values.studentIdentifier]);

  useEffect(() => {
    const currentDistrictSchools = find(formView.districts, {
      districtId: getDistrictId,
    });
    const is200CteSchoolExists = find(currentDistrictSchools?.schools, {
      schoolId: `${getDistrictId}-200`,
    });
    const is500SpedSchoolExists = find(currentDistrictSchools?.schools, {
      schoolId: `${getDistrictId}-500`,
    });

    if (
      (dataEntryMode === DataEntryMode.NEW ||
        dataEntryMode === DataEntryMode.EDIT) &&
      isMsisIdAndSchoolTypeNotEmpty
    ) {
      const listItemRecord: NonPublicStudentListItemModel | undefined = find(
        listItem,
        {
          studentIdentifier: values.studentIdentifier,
          schoolType: values.schoolType,
        }
      );

      if (
        dirty &&
        npsInput.schoolType !== values.schoolType &&
        listItemRecord !== undefined
      ) {
        setSearchMessage(
          'A student with this MSIS ID already has an entry.  Please check the MSIS ID and try again.'
        );

        setFieldValue('schoolTypeInitial', '');
      } else {
        setSearchMessage('');
      }
    }

    if (values.schoolType === 'CTE' && is200CteSchoolExists === undefined) {
      setSearchMessage(
        'School 200 does not exist in your LEA. Please contact the MSIS helpdesk.'
      );
    }

    if (values.schoolType.toUpperCase() === 'SpecEd'.toUpperCase() && is500SpedSchoolExists === undefined) {
      setSearchMessage(
        'School 500 does not exist in your LEA. Please contact the MSIS helpdesk.'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.schoolType, values.studentIdentifier]);

  const searchDisabled =
    dataEntryMode === DataEntryMode.EDIT ||
    !getIsMsisIDValid() ||
    searching ||
    hasSearchMatch;

    const lookupSearchMessage = (status: string): string => {
      if (status === '404') {
        return 'MSIS ID Not Found. Please ensure the MSIS ID has been created for the student.';
      } else if (status === '403') {
        return 'This student is not enrolled in the selected/assigned LEA and a school you are assigned to. Please check the MSIS ID and try again';
      } else{
        return '';
      }
    };

  const thisHandlerOnlySatisfiesTheModal = (): boolean => {return true;}

  const handleMSISSearch = async (): Promise<void> => {
    const setUserFields = (data: NonPublicStudentSearchResultModel): void => {
      setFieldValue('firstName', data.firstName);
      setFieldValue('lastOrSurname', data.lastOrSurname);
      /* istanbul ignore next */
      setFieldValue('middleName', data.middleName || '');
      setFieldValue('birthDate', data.birthDate);
      /* istanbul ignore next */
      const formattedSSN = formatSSNForDisplay(data.personIdentifierSsn || '');
      setFieldValue('personIdentifierSsn', formattedSSN);

      setFieldValue('hasMatch', true);
    };

    setSearching(true);
    dispatch(setHasSearchMatch(false));
    dispatch(setHas200500SearchMatch(false));
    setHasSearchMatchState(false);
    setSearchMessage('');

    const response = await getStudentByMSISId(values.studentIdentifier);
    if (isValidCode(response.status)) {
      const schoolIdPattern = /^\d{4}-\d{3}$/;
      const schoolId: string | undefined = response.data.schoolId;
      const districtName: string | undefined = response.data.districtName;
      setUserFields(response.data);
      setFieldValue('ideaEducationalEnvironment', '');
      if (schoolId && districtName && schoolIdPattern.test(schoolId)) {
        setLeaNameOfMsisFoundStudent(districtName);
        if (!schoolId?.endsWith('-200') && !schoolId?.endsWith('-500')) {
          setShowStudentEnrolled(true);
          setHasNon200500SearchMatchState(true);
        }
        else {
          dispatch(setHasSearchMatch(true));
          setHasSearchMatchState(true);
        }
      }
      if (!schoolId  || !districtName ) {
          dispatch(setHasSearchMatch(true));
          setHasSearchMatchState(true);
          setUserFields(response.data);
      }
    } else {
      const errorMessage = lookupSearchMessage(response.status.toString());
      setSearchMessage( errorMessage );
    }
    setSearching(false);
  };

  const handleMsisValueChange = (): Promise<void> => {
    const resetMatchedFields = (): void => {
      setFieldValue('firstName', '');
      setFieldValue('lastOrSurname', '');
      setFieldValue('middleName', '');
      setFieldValue('birthDate', '');
      setFieldValue('personIdentifierSsn', '');

      setFieldValue('schoolType', '');

      setFieldValue('sex', '');
      setFieldValue('hispanicOrLatinoEthnicity', '');
      setFieldValue('race', '');
      setFieldValue('specialEducation', '');
      setFieldValue('englishLearnerStatus', '');

      setFieldValue('enrollmentEntryDate', '');
      setFieldValue('enrollmentStatus', '');
      setFieldValue('gradeLevel', '');

      setFieldValue('hasMatch', false);

      setFieldTouched('firstName', false);
      setFieldTouched('lastOrSurname', false);
      setFieldTouched('birthDate', false);
      setFieldTouched('birthDate', false);


      setFieldTouched('schoolType', false);

      setFieldTouched('sex', false);
      setFieldTouched('hispanicOrLatinoEthnicity', false);
      setFieldTouched('race', false);
      setFieldTouched('specialEducation', false);
      setFieldTouched('englishLearnerStatus', false);

      setFieldTouched('enrollmentEntryDate', false);
      setFieldTouched('enrollmentStatus', false);
      setFieldTouched('gradeLevel', false);


    };

    setSearchMessage('');

    // Change after match
    if (hasSearchMatch) {
      resetMatchedFields();
      dispatch(setHasSearchMatch(false));
      setHasSearchMatchState(false);
    }

    validateField('studentIdentifier');
    return Promise.resolve();
  };

  const hideStudentEnrolledModal = (): void => {
    setShowStudentEnrolled(false);
  }


  return (
    <>
      <Panel panelType={PanelType.INFO} heading="Student Information">
        <div className="field-row field-row-msis-id">
          <div>
            <FormTextbox
              displayName="Enter MSIS ID"
              field="studentIdentifier"
              validationSchema={NonPublicStudentEntryValidationSchema}
              disabled={dataEntryMode === DataEntryMode.EDIT}
              onChangeEvent={handleMsisValueChange}
            />
          </div>
          <div className="search-button">
            <ActionButton
              classes="button--secondary"
              size={ButtonSize.SMALL}
              onClick={handleMSISSearch}
              disabled={searchDisabled}
              dataTestId="student-search-button"
              tooltipText="Click to search for student"
              loading={searching}
            >
              Search
            </ActionButton>
          </div>
          <div className="search-message" data-testid="search-error-message">
            {searchMessage}
          </div>
        </div>

        <div className="field-row field-row-msis-search-results">
          <FormTextbox
            displayName="Last Name"
            field="lastOrSurname"
            disabled={areNonMsisFormComponentsDisabled}
            validationSchema={NonPublicStudentEntryValidationSchema}
          />
          <FormTextbox
            displayName="First Name"
            field="firstName"
            disabled={areNonMsisFormComponentsDisabled}
            validationSchema={NonPublicStudentEntryValidationSchema}
          />
          <FormTextbox
            displayName="Middle Name"
            field="middleName"
            disabled={areNonMsisFormComponentsDisabled}
          />
          <FormDateSelector
            displayName="Date of Birth"
            field="birthDate"
            placeholderText="mm/dd/yyyy"
            disabled={areNonMsisFormComponentsDisabled}
            validationSchema={NonPublicStudentEntryValidationSchema}
            maxDate={MAX_DATE}
            isClearable
          />
          <FormTextbox
            displayName="SSN"
            field="personIdentifierSsn"
            disabled={areNonMsisFormComponentsDisabled}
            onChangeEvent={async (config) => {
              const previousValue = values.personIdentifierSsn || '';
              const displayValue = formatSSNForDisplay(
                config.value,
                previousValue
              );
              if (config.setValueHook) {
                config.setValueHook('personIdentifierSsn', displayValue);
              }
            }}
          />
        </div>
      </Panel>
      {showStudentEnrolled && (
        <Modal
          type={DialogType.CONFIRM}
          open={true}
          onClose={thisHandlerOnlySatisfiesTheModal}
          title={'Student is already enrolled'}
        >
          <div className="confirm-container" data-testid="confirmation-modal">
            <p data-testid="cancel-message">
              This student is enrolled in {leaNameOfMsisFoundStudent} and cannot
              be enrolled in School 200/500.
            </p>
            <div className="button-row">
              <ActionButton
                onClick={hideStudentEnrolledModal}
                dataTestId="yes-button"
                tooltipText={'Navigate to the last page'}
              >
                <span>Ok</span>
              </ActionButton>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default NPSE_StudentPanel;
